// @ts-nocheck
/**
 * Manage Blacklist / DND — redesigned to obd3-gui pattern (ScrollPage +
 * PageHeader + Card). 10-digit mobile validation; add-to branches to
 * API_DND_ADD (operation:'add') vs API_BLACKLIST_ADD.
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2, Save, ShieldAlert, Ban, PhoneOff } from 'lucide-react';
import { postApiCall } from '@/service/httpCall';
import { API_DND_ADD, API_BLACKLIST_ADD } from '@/service/url';
import { ROUTES } from '@/utilities/constant';
import { Button } from '@/components/ui/button';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';

const ManageBlacklistDnd = () => {
  const [mobile, setMobile] = useState('');
  const [addTo, setAddTo] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleMobileChange = (e: any) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 10);
    setMobile(val);
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    if (!mobile || mobile.length !== 10) {
      toast.error('Please enter a valid 10-digit mobile number');
      return;
    }
    if (!addTo) {
      toast.error('Please select an option from the Add to dropdown');
      return;
    }

    setLoading(true);
    try {
      if (addTo === 'DND') {
        await postApiCall(API_DND_ADD, { mobile: Number(mobile), operation: 'add' });
        toast.success('Number added to DND successfully');
        navigate(`/${ROUTES.NUMBER_OPERATIONS.VIEW_BLACKLIST_DND}`);
      } else if (addTo === 'Blacklist') {
        await postApiCall(API_BLACKLIST_ADD, { mobile: Number(mobile) });
        toast.success('Number added to Blacklist successfully');
        navigate(`/${ROUTES.NUMBER_OPERATIONS.VIEW_BLACKLIST_DND}`);
      }
    } catch (err: any) {
      toast.error(friendlyApiError(err) || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ScrollPage>
      <PageHeader
        title="Manage Blacklist / DND"
        description="Add a mobile number to the DND or Blacklist so campaigns skip it."
        actions={
          <Button asChild variant="outline">
            <Link to={`/${ROUTES.NUMBER_OPERATIONS.VIEW_BLACKLIST_DND}`}>
              <ShieldAlert className="h-4 w-4" />
              View list
            </Link>
          </Button>
        }
      />

      <div
        className="rounded-2xl border border-border/60 bg-card p-5"
        style={{
          boxShadow:
            '6px 6px 14px rgba(163, 177, 198, 0.35), -6px -6px 14px rgba(255, 255, 255, 0.9)',
        }}
      >
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <label htmlFor="mobile" className="text-xs font-semibold text-foreground">
                Mobile number <span className="text-destructive">*</span>
              </label>
              <input
                id="mobile"
                type="text"
                inputMode="numeric"
                placeholder="10-digit mobile number"
                value={mobile}
                onChange={handleMobileChange}
                maxLength={10}
                className="input-embossed h-10 w-full rounded-md px-3 font-mono text-sm tracking-wider"
              />
              <p className="text-[11px] text-muted-foreground">
                Digits only — non-numeric characters are stripped.
              </p>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-foreground">
                Add to <span className="text-destructive">*</span>
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setAddTo('Blacklist')}
                  className={`relative flex h-11 items-center justify-center gap-2 rounded-lg border-2 text-sm font-bold transition ${
                    addTo === 'Blacklist'
                      ? 'border-rose-500 bg-rose-500 text-white shadow-md ring-2 ring-rose-500/25'
                      : 'border-rose-200 bg-rose-50 text-rose-700 hover:border-rose-400 hover:bg-rose-100 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-300'
                  }`}
                >
                  <Ban className="h-4 w-4" />
                  Blacklist
                  {addTo === 'Blacklist' && (
                    <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-white text-rose-600 ring-2 ring-rose-500">
                      <svg viewBox="0 0 24 24" className="h-2.5 w-2.5" fill="none" stroke="currentColor" strokeWidth="3">
                        <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setAddTo('DND')}
                  className={`relative flex h-11 items-center justify-center gap-2 rounded-lg border-2 text-sm font-bold transition ${
                    addTo === 'DND'
                      ? 'border-amber-500 bg-amber-500 text-white shadow-md ring-2 ring-amber-500/25'
                      : 'border-amber-200 bg-amber-50 text-amber-700 hover:border-amber-400 hover:bg-amber-100 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-300'
                  }`}
                >
                  <PhoneOff className="h-4 w-4" />
                  DND
                  {addTo === 'DND' && (
                    <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-white text-amber-600 ring-2 ring-amber-500">
                      <svg viewBox="0 0 24 24" className="h-2.5 w-2.5" fill="none" stroke="currentColor" strokeWidth="3">
                        <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                  )}
                </button>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Campaigns skip numbers on either list.
              </p>
            </div>
          </div>

          <div className="mt-5 flex items-center justify-end gap-2 border-t border-border/50 pt-4">
            <Button asChild variant="outline">
              <Link to={`/${ROUTES.NUMBER_OPERATIONS.VIEW_BLACKLIST_DND}`}>Cancel</Link>
            </Button>
            <Button type="submit" disabled={loading} className="gap-1.5">
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {loading ? 'Submitting…' : 'Add number'}
            </Button>
          </div>
        </form>
      </div>
    </ScrollPage>
  );
};

export default ManageBlacklistDnd;
